import { CSSProperties, HTMLAttributes, ReactNode } from 'react';
import { breakpoints } from 'styles';
import { useReduxState } from 'hooks';
import { RadioInput } from '.';

export type RadioGroupOption = {
  disabled?: boolean;
  id: string;
  label: ReactNode;
  labelAdditionalContent?: ReactNode;
  tooltip?: string;
  tooltipHideIcon?: boolean;
  value: any;
};

interface Props extends HTMLAttributes<HTMLElement> {
  children?: ReactNode;
  darkMode?: boolean;
  error?: string;
  inline?: boolean;
  inlineBreakpoint?: number;
  inlineSpacing?: string;
  labelStyle?: CSSProperties;
  onChange?: (value: any) => void;
  options: RadioGroupOption[];
  spacing?: string;
  stopPropagation?: boolean;
  style?: CSSProperties;
  styleAsCards?: boolean;
  value: any;
}

const RadioGroupInput = ({
  children,
  darkMode,
  error,
  inline,
  inlineBreakpoint = breakpoints.medium,
  inlineSpacing = '25px',
  labelStyle,
  onChange,
  options,
  spacing = '5px',
  stopPropagation = true,
  style,
  styleAsCards,
  value
}: Props) => {
  const { theme } = useReduxState();

  let color = 'rgba(0, 0, 0, 0.1)';
  let iconColor = theme.successTextColor;
  let activeColor = theme.successColor;
  let outlineColor = 'rgba(0, 0, 0, 0.15)';
  let labelColor = theme.textColorLight;
  if (darkMode) {
    color = 'rgba(255, 255, 255, 0.5)';
    iconColor = theme.successTextColorDark;
    activeColor = theme.successColorDark;
    outlineColor = 'rgba(255, 255, 255, 0.2)';
    labelColor = '#fff';
  }

  const handleClick = (ev) => ev.stopPropagation();

  return (
    <div className="radio-group" style={style}>
      {options
        ? options.map((option) => (
            <div
              className={`radio-option ${inline ? ' inline' : ''}`}
              key={option.id}
              {...(stopPropagation ? { onClick: handleClick } : {})}
            >
              <RadioInput
                checked={value === option.value}
                darkMode={darkMode}
                disabled={option.disabled}
                id={option.id}
                label={option.label}
                labelAdditionalContent={option.labelAdditionalContent}
                labelStyle={labelStyle}
                onChange={onChange}
                stopPropagation={stopPropagation}
                styleAsCard={styleAsCards}
                tooltip={option.tooltip}
                tooltipHideIcon={option.tooltipHideIcon}
                value={option.value}
              />
            </div>
          ))
        : null}

      {children ? children : null}

      {error ? <div className="error-message">{error}</div> : null}

      <style jsx>{`
        .radio-group {
          position: relative;
        }
        .radio-option:not(:first-child) {
          margin-top: ${spacing};
        }
        .error-message {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
        @media (min-width: ${inlineBreakpoint}px) {
          .radio-option.inline {
            display: inline-block;
            margin-top: 0;
          }
          .radio-option.inline:not(:first-child) {
            margin-left: ${inlineSpacing};
          }
        }
      `}</style>
    </div>
  );
};

export default RadioGroupInput;
